import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Activity, Heart, User, Calendar } from 'lucide-react';
import { theme } from '../../theme';
import { API_BASE_URL, isGuestMode } from '../../config/api';

const MOCK_ACTIVITIES = [
  { id: 1, user_id: 2, text: 'BudgetBrawler logged a no-spend day', date: new Date(Date.now() - 86400000).toISOString(), kudos_count: 5, has_kudos: false },
  { id: 2, user_id: 3, text: 'DebtDestroyer hit a 14-day streak!', date: new Date(Date.now() - 172800000).toISOString(), kudos_count: 12, has_kudos: true },
  { id: 3, user_id: 0, text: 'DebtSlayer logged a no-spend day', date: new Date(Date.now() - 259200000).toISOString(), kudos_count: 3, has_kudos: false },
  { id: 4, user_id: 4, text: 'SavageSaver crushed 50% of their debt!', date: new Date(Date.now() - 345600000).toISOString(), kudos_count: 22, has_kudos: false },
  { id: 5, user_id: 5, text: 'PennyPuncher completed the 7-Day Streak segment', date: new Date(Date.now() - 432000000).toISOString(), kudos_count: 8, has_kudos: true },
];

interface ArenaFeedProps {
  user: { id: number; username: string; email: string };
}

export function ArenaFeed({ user }: ArenaFeedProps) {
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const guest = isGuestMode();

  useEffect(() => {
    if (guest) {
      setActivities(MOCK_ACTIVITIES);
      setLoading(false);
      return;
    }
    fetchFeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFeed = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/arena/activities/feed?limit=30`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setActivities(data.activities);
      }
    } catch (err) {
      console.error('Failed to fetch feed:', err);
    } finally {
      setLoading(false);
    }
  };

  const giveKudos = async (activityId: number) => {
    if (guest) return;
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    try {
      await fetch(`${API_BASE_URL}/arena/social/kudos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ activity_id: activityId }),
      });
      fetchFeed();
    } catch (err) {
      console.error('Failed to give kudos:', err);
    }
  };

  if (loading) return <div>Loading activity feed...</div>;

  return (
    <div>
      <h2>Activity Feed</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[4] }}>
        {activities.length === 0 ? (
          <div style={{ textAlign: 'center', padding: theme.spacing[8] }}>
            No activities yet. Start tracking your progress!
          </div>
        ) : (
          activities.map((activity) => (
            <motion.div
              key={activity.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              style={{
                background: theme.semantic.bg.surface,
                border: `1px solid ${theme.semantic.border.default}`,
                borderRadius: theme.radii.md,
                padding: theme.spacing[5],
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing[4],
              }}
            >
              <Activity size={24} color={theme.brand.copper} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
                  <User size={14} />
                  <span>{activity.text}</span>
                </div>
                <div style={{ display: 'flex', gap: theme.spacing[3], color: theme.semantic.text.secondary }}>
                  <span><Calendar size={12} /> {new Date(activity.date).toLocaleDateString()}</span>
                  {activity.kudos_count > 0 && <span><Heart size={12} /> {activity.kudos_count}</span>}
                </div>
              </div>
              {activity.user_id !== user.id && !activity.has_kudos && (
                <button
                  onClick={() => giveKudos(activity.id)}
                  style={{
                    background: 'transparent',
                    border: `2px solid ${theme.semantic.border.default}`,
                    borderRadius: theme.radii.md,
                    padding: theme.spacing[2],
                    cursor: 'pointer',
                    color: theme.brand.copper,
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = theme.semantic.bg.elevated;
                    e.currentTarget.style.borderColor = theme.brand.copper;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = theme.semantic.border.default;
                  }}
                >
                  <Heart size={18} />
                </button>
              )}
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}